/**
 * CheckinPage — gate-staff QR verification + entry. Route: /checkin
 * Auth + CHECKIN_VERIFY permission required (gated in App.tsx).
 *
 * Flow: scan QR (native BarcodeDetector when available) or type the guest code →
 * verify (read-only) → Allow Entry confirms and records the check-in. Duplicate
 * scans surface a prominent "already checked in" warning (no second entry).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  confirmCheckin,
  verifyCheckin,
  type VerifyCheckinResponse,
} from '@/src/modules/guests/api';

type Result =
  | { kind: 'idle' }
  | { kind: 'verifying' }
  | { kind: 'verified'; code: string; data: VerifyCheckinResponse }
  | { kind: 'confirmed'; name?: string; at: string }
  | { kind: 'error'; message: string };

function formatTime(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium', timeStyle: 'short' }).format(d);
}

export function CheckinPage() {
  const [code, setCode] = useState('');
  const [result, setResult] = useState<Result>({ kind: 'idle' });
  const [scanning, setScanning] = useState(false);

  const runVerify = useCallback(async (guestCode: string) => {
    const trimmed = guestCode.trim();
    if (!trimmed) return;
    setResult({ kind: 'verifying' });
    try {
      const data = await verifyCheckin(trimmed);
      setResult({ kind: 'verified', code: trimmed, data });
    } catch (e) {
      setResult({ kind: 'error', message: (e as Error).message });
    }
  }, []);

  const handleConfirm = async (guestCode: string, name?: string) => {
    try {
      const res = await confirmCheckin(guestCode);
      setResult({ kind: 'confirmed', name, at: res.checkedInAt });
    } catch (e) {
      const err = e as Error & { status?: number };
      if (err.status === 409) {
        // Someone else already checked them in between verify and confirm.
        await runVerify(guestCode);
      } else {
        setResult({ kind: 'error', message: err.message });
      }
    }
  };

  const reset = () => {
    setCode('');
    setResult({ kind: 'idle' });
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white p-4 flex flex-col items-center">
      <div className="w-full max-w-md">
        <h1 className="text-xl font-bold text-center py-4">Gate Check-in</h1>

        <CameraScanner
          active={scanning}
          onToggle={() => setScanning((s) => !s)}
          onDetected={(c) => {
            setScanning(false);
            setCode(c);
            void runVerify(c);
          }}
        />

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void runVerify(code);
          }}
          className="flex gap-2 mt-4"
        >
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Enter guest code"
            className="flex-1 rounded-lg px-3 py-3 text-slate-900 text-sm"
            autoFocus
          />
          <button
            type="submit"
            className="bg-indigo-500 px-4 py-3 rounded-lg text-sm font-medium hover:bg-indigo-600"
          >
            Verify
          </button>
        </form>

        <div className="mt-6">
          <ResultCard result={result} onConfirm={handleConfirm} onReset={reset} />
        </div>
      </div>
    </div>
  );
}

function ResultCard({
  result,
  onConfirm,
  onReset,
}: {
  result: Result;
  onConfirm: (code: string, name?: string) => void;
  onReset: () => void;
}) {
  if (result.kind === 'idle') {
    return (
      <p className="text-center text-slate-400 text-sm">
        Scan a pass or enter a code to begin.
      </p>
    );
  }

  if (result.kind === 'verifying') {
    return (
      <div className="flex justify-center py-6">
        <div className="w-8 h-8 rounded-full border-4 border-indigo-400 border-t-transparent animate-spin" />
      </div>
    );
  }

  if (result.kind === 'error') {
    return (
      <Card tone="red" title="Error">
        <p className="text-sm">{result.message}</p>
        <ResetButton onReset={onReset} />
      </Card>
    );
  }

  if (result.kind === 'confirmed') {
    return (
      <Card tone="green" title="✅ Entry allowed">
        {result.name && <p className="text-lg font-semibold">{result.name}</p>}
        <p className="text-sm opacity-80">Checked in at {formatTime(result.at)}</p>
        <ResetButton onReset={onReset} label="Next guest" />
      </Card>
    );
  }

  // verified
  const { data, code } = result;

  if (!data.valid) {
    return (
      <Card tone="red" title="❌ Invalid code">
        <p className="text-sm">No guest matches this code.</p>
        <ResetButton onReset={onReset} />
      </Card>
    );
  }

  if (data.alreadyCheckedIn) {
    return (
      <Card tone="yellow" title="⚠ Already checked in">
        <p className="text-lg font-semibold">{data.guestName}</p>
        <p className="text-sm opacity-80">
          Entered at {formatTime(data.checkedInAt)}
          {data.checkedInBy ? ` · by ${data.checkedInBy}` : ''}
        </p>
        <p className="text-xs mt-2 font-medium">Do not allow a second entry.</p>
        <ResetButton onReset={onReset} label="Next guest" />
      </Card>
    );
  }

  return (
    <Card tone="green" title="Guest verified">
      <p className="text-lg font-semibold">{data.guestName}</p>
      {data.eventName && <p className="text-sm opacity-80">{data.eventName}</p>}
      <button
        onClick={() => onConfirm(code, data.guestName)}
        className="mt-4 w-full bg-emerald-500 hover:bg-emerald-600 text-white py-3 rounded-lg font-semibold"
      >
        Allow Entry
      </button>
      <ResetButton onReset={onReset} />
    </Card>
  );
}

const TONE: Record<string, string> = {
  green: 'bg-emerald-600',
  yellow: 'bg-amber-500',
  red: 'bg-red-600',
};

function Card({
  tone,
  title,
  children,
}: {
  tone: 'green' | 'yellow' | 'red';
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`${TONE[tone]} rounded-2xl p-5 text-white text-center`}>
      <h2 className="font-bold text-lg mb-2">{title}</h2>
      {children}
    </div>
  );
}

function ResetButton({ onReset, label = 'Scan another' }: { onReset: () => void; label?: string }) {
  return (
    <button
      onClick={onReset}
      className="mt-3 text-sm underline opacity-90 hover:opacity-100"
    >
      {label}
    </button>
  );
}

/**
 * Camera scanner using the native BarcodeDetector API when the browser supports
 * it (Chrome/Android). On unsupported browsers it stays hidden and gate staff
 * use manual code entry — no third-party scanner dependency required.
 */
function CameraScanner({
  active,
  onToggle,
  onDetected,
}: {
  active: boolean;
  onToggle: () => void;
  onDetected: (code: string) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [supported] = useState(
    () => typeof window !== 'undefined' && 'BarcodeDetector' in window,
  );
  const [camError, setCamError] = useState<string | null>(null);

  useEffect(() => {
    if (!active || !supported) return;
    let stream: MediaStream | null = null;
    let raf = 0;
    let cancelled = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const detector = new (window as any).BarcodeDetector({ formats: ['qr_code'] });

    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });
        if (cancelled) return;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        const tick = async () => {
          if (cancelled || !videoRef.current) return;
          try {
            const codes = await detector.detect(videoRef.current);
            if (codes.length > 0 && codes[0].rawValue) {
              onDetected(String(codes[0].rawValue));
              return;
            }
          } catch {
            /* transient detect errors are ignored */
          }
          raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
      } catch (e) {
        setCamError((e as Error).message || 'Camera unavailable');
      }
    })();

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [active, supported, onDetected]);

  if (!supported) {
    return (
      <p className="text-center text-xs text-slate-400">
        Camera scanning isn’t supported on this browser — enter the code manually.
      </p>
    );
  }

  return (
    <div>
      <button
        onClick={onToggle}
        className="w-full bg-slate-700 hover:bg-slate-600 py-2 rounded-lg text-sm font-medium"
      >
        {active ? 'Stop camera' : '📷 Scan with camera'}
      </button>
      {active && (
        <div className="mt-3 rounded-xl overflow-hidden bg-black aspect-square">
          <video ref={videoRef} className="w-full h-full object-cover" muted playsInline />
        </div>
      )}
      {camError && <p className="text-center text-xs text-red-400 mt-2">{camError}</p>}
    </div>
  );
}
